import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideDownload,
  lucideRotateCw,
  lucideTriangleAlert,
  lucideWallet,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmNativeSelectImports } from '@spartan-ng/helm/native-select';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import {
  AccountingService,
  PayoutSummary,
  downloadErrorMessage,
  saveDownload,
} from '../../../services/accounting.service';
import { formatCurrency } from '../../../utils/currency.utils';

/** What the merchant should know about a payout that isn't fully broken down. */
const RECONCILIATION_HINTS: Record<string, string> = {
  pending: 'wird aufgeschlüsselt',
  incomplete: 'teilweise zugeordnet',
  unavailable: 'Sofortauszahlung – ohne Aufschlüsselung',
};

/**
 * The shop's Stripe payouts with one statement per payout: the proof that goes next
 * to the matching credit on the bank statement.
 */
@Component({
  selector: 'app-payout-statements',
  standalone: true,
  imports: [
    FormsModule,
    NgIcon,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmNativeSelectImports,
    HlmSpinnerImports,
    HlmTableImports,
    HlmTypographyImports,
  ],
  providers: [provideIcons({ lucideDownload, lucideRotateCw, lucideTriangleAlert, lucideWallet })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  templateUrl: './payout-statements.component.html',
})
export class PayoutStatementsComponent {
  private readonly accounting = inject(AccountingService);

  private readonly currentYear = new Date().getFullYear();
  readonly years = Array.from({ length: 5 }, (_, i) => this.currentYear - i);
  readonly selectedYear = signal(this.currentYear);

  readonly payouts = signal<PayoutSummary[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  /** The running download, e.g. "po_123-pdf"; disables its button. */
  readonly busy = signal<string | null>(null);
  readonly downloadError = signal<string | null>(null);

  constructor() {
    this.load();
  }

  euro(cents: number): string {
    return `${formatCurrency(cents)} €`;
  }

  /** "2026-09-05" -> "05.09.2026" (a calendar day: no time zone to convert). */
  day(isoDate: string): string {
    return isoDate.split('-').reverse().join('.');
  }

  hint(payout: PayoutSummary): string | null {
    return RECONCILIATION_HINTS[payout.reconciliation] ?? null;
  }

  selectYear(year: number | string): void {
    this.selectedYear.set(Number(year));
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.accounting.listPayouts(this.selectedYear()).subscribe({
      next: (payouts) => {
        this.payouts.set(payouts);
        this.loading.set(false);
      },
      error: (err) => {
        this.payouts.set([]);
        this.loading.set(false);
        this.error.set(err.error?.detail || 'Die Auszahlungen konnten nicht geladen werden.');
      },
    });
  }

  download(payout: PayoutSummary, format: 'pdf' | 'csv'): void {
    const key = `${payout.stripe_payout_id}-${format}`;
    this.busy.set(key);
    this.downloadError.set(null);
    this.accounting.downloadPayoutStatement(payout.stripe_payout_id, format).subscribe({
      next: (download) => {
        this.busy.set(null);
        saveDownload(
          download,
          `schenkwerk-auszahlung-${payout.arrival_date}-${payout.stripe_payout_id}.${format}`,
        );
      },
      error: async (err) => {
        this.busy.set(null);
        this.downloadError.set(await downloadErrorMessage(err));
      },
    });
  }
}
